import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  HOUSEKEEPING_NOTE_MAX_LENGTH,
  HOUSEKEEPING_TASK_STATUS_LABELS,
  isOpenTask,
} from '@wodi/domain';
import type { HousekeepingTaskView } from '@wodi/shared';
import {
  Button,
  Dialog,
  DialogActions,
  Notice,
  Section,
  Select,
  TextArea,
  TextField,
} from '@wodi/ui';
import { useState } from 'react';

import {
  addConsumable,
  completeTask,
  consumableOptionsQuery,
  housekeepingKeys,
  skipTask,
  startTask,
} from '../api/housekeeping.ts';
import { useOnline } from '../hooks/use-online.ts';
import { dateTimeText, errorText } from '../operations/format.tsx';
import styles from '../operations/operations.module.css';
import { toasts } from '../toasts.ts';
import { taskPlace } from './words.tsx';

// One housekeeping task (docs/plans/phase3-kitchen-and-housekeeping.md §2.4): start it, mark
// it done with a note, skip a scheduled one with a reason, and record consumables used, which
// leave stock through the ledger. No patient is shown: a terminal clean shows its precautions.

function Consumables({
  task,
  onChanged,
}: {
  task: HousekeepingTaskView;
  onChanged: (next: HousekeepingTaskView) => void;
}) {
  const online = useOnline();
  const queryClient = useQueryClient();
  const [storeId, setStoreId] = useState<string | null>(null);
  const [itemId, setItemId] = useState<string | null>(null);
  const [quantity, setQuantity] = useState('1');
  const options = useQuery(consumableOptionsQuery(storeId));
  const qty = Number(quantity.trim());
  const valid = storeId !== null && itemId !== null && Number.isInteger(qty) && qty > 0;
  const add = useMutation({
    mutationFn: () =>
      addConsumable(task.id, {
        storeId: storeId ?? '',
        itemId: itemId ?? '',
        quantity: qty,
        batchId: null,
        batchOverrideReason: null,
      }),
    onSuccess: async (next) => {
      onChanged(next);
      await queryClient.invalidateQueries({ queryKey: housekeepingKeys.all });
      toasts.show({ title: 'Consumable recorded' });
      setItemId(null);
      setQuantity('1');
    },
  });
  const item = options.data?.items.find((i) => i.itemId === itemId);
  return (
    <Section
      title="Consumables used"
      description="Taken from the store's stock, earliest expiry first."
      headingLevel={3}
    >
      <div className={styles.form}>
        {task.consumables.length === 0 ? (
          <p className={styles.meta}>None recorded.</p>
        ) : (
          <ul className={styles.list} aria-label="Consumables used">
            {task.consumables.map((c) => (
              <li key={c.id} className={styles.item}>
                <span>{`${c.itemName}: ${String(c.quantity)} ${c.unit}`}</span>
                <span className={styles.meta}>
                  {`${c.storeName}, batch `}
                  <span className="wodi-id">{c.batchNumber}</span>
                  {`, ${dateTimeText(c.recordedAt)}`}
                </span>
              </li>
            ))}
          </ul>
        )}
        <div className={styles.fields}>
          <Select
            label="Store"
            placeholder="Choose a store"
            options={(options.data?.stores ?? []).map((s) => ({ id: s.id, label: s.name }))}
            value={storeId}
            onChange={(key) => {
              setStoreId(key === null ? null : String(key));
              setItemId(null);
            }}
          />
          <Select
            label="Item"
            placeholder={storeId === null ? 'Choose a store first' : 'Choose an item'}
            isDisabled={storeId === null}
            options={(options.data?.items ?? []).map((i) => ({
              id: i.itemId,
              label: `${i.name} (${String(i.available)} ${i.unit} in stock)`,
            }))}
            value={itemId}
            onChange={(key) => {
              setItemId(key === null ? null : String(key));
            }}
          />
          <TextField
            label="Quantity"
            inputMode="numeric"
            unit={item?.unit ?? ''}
            value={quantity}
            onChange={setQuantity}
          />
        </div>
        {add.error === null ? null : (
          <Notice tone="error">{errorText(add.error, "It wasn't recorded. Try again.")}</Notice>
        )}
        <div className={styles.actions}>
          <Button
            isDisabled={!online || !valid}
            isPending={add.isPending}
            pendingLabel="Recording…"
            onPress={() => {
              add.mutate();
            }}
          >
            Record consumable
          </Button>
        </div>
      </div>
    </Section>
  );
}

export function TaskDialog({
  task: shown,
  mayRecord,
  onClose,
}: {
  task: HousekeepingTaskView;
  mayRecord: boolean;
  onClose: () => void;
}) {
  // The task as last returned by the server, so the dialog follows its own steps.
  const [task, setTask] = useState(shown);
  const online = useOnline();
  const queryClient = useQueryClient();
  const [note, setNote] = useState('');
  const [skipping, setSkipping] = useState(false);
  const [reason, setReason] = useState('');
  const isBed = task.kind !== 'scheduled';
  const done = async (title: string) => {
    await queryClient.invalidateQueries({ queryKey: housekeepingKeys.all });
    await queryClient.invalidateQueries({ queryKey: ['inpatient', 'bed-board'] });
    toasts.show({ title, description: taskPlace(task) });
  };
  const start = useMutation({
    mutationFn: () => startTask(task.id),
    onSuccess: async (next) => {
      setTask(next);
      await done('Cleaning started');
    },
  });
  const complete = useMutation({
    mutationFn: () => completeTask(task.id, note.trim() === '' ? null : note.trim()),
    onSuccess: async () => {
      await done(isBed ? 'Bed marked clean' : 'Task done');
      onClose();
    },
  });
  const skip = useMutation({
    mutationFn: () => skipTask(task.id, reason.trim()),
    onSuccess: async () => {
      await done('Task skipped');
      onClose();
    },
  });
  const open = isOpenTask(task.status);
  const error = start.error ?? complete.error ?? skip.error;
  return (
    <Dialog
      title={task.title}
      isOpen
      onOpenChange={(isOpen) => {
        if (!isOpen) onClose();
      }}
    >
      <div className={styles.form}>
        <dl className={styles.facts}>
          <dt>Where</dt>
          <dd>{taskPlace(task)}</dd>
          {task.precautions.length > 0 ? (
            <>
              <dt>Precautions</dt>
              <dd>{task.precautions.join(', ')}</dd>
            </>
          ) : null}
          <dt>Due</dt>
          <dd>{dateTimeText(task.dueAt)}</dd>
          <dt>Status</dt>
          <dd>{HOUSEKEEPING_TASK_STATUS_LABELS[task.status]}</dd>
          {task.startedAt === null ? null : (
            <>
              <dt>Started</dt>
              <dd>{`${dateTimeText(task.startedAt)} by ${task.startedBy?.displayName ?? ''}`}</dd>
            </>
          )}
          {task.completedAt === null ? null : (
            <>
              <dt>Done</dt>
              <dd>{`${dateTimeText(task.completedAt)} by ${task.completedBy?.displayName ?? ''}`}</dd>
            </>
          )}
          {task.statusReason === null ? null : (
            <>
              <dt>Reason</dt>
              <dd>{task.statusReason}</dd>
            </>
          )}
          {task.note === null ? null : (
            <>
              <dt>Note</dt>
              <dd className={styles.prose}>{task.note}</dd>
            </>
          )}
        </dl>
        {mayRecord && open && !skipping ? (
          <TextArea
            label="Note (optional)"
            description="For example consumables not kept in stock."
            value={note}
            onChange={setNote}
            maxLength={HOUSEKEEPING_NOTE_MAX_LENGTH}
          />
        ) : null}
        {skipping ? (
          <TextArea
            label="Why is it skipped?"
            description="At least 5 characters."
            value={reason}
            onChange={setReason}
            autoFocus
          />
        ) : null}
        {error === null ? null : (
          <Notice tone="error">{errorText(error, "It wasn't saved. Try again.")}</Notice>
        )}
        {mayRecord && task.status !== 'skipped' && task.status !== 'overridden' ? (
          <Consumables task={task} onChanged={setTask} />
        ) : null}
      </div>
      <DialogActions>
        <Button variant="quiet" onPress={onClose}>
          Close
        </Button>
        {mayRecord && open && !isBed && !skipping ? (
          <Button
            isDisabled={!online}
            onPress={() => {
              setSkipping(true);
            }}
          >
            Skip task
          </Button>
        ) : null}
        {mayRecord && skipping ? (
          <Button
            variant="primary"
            isDisabled={!online || reason.trim().length < 5}
            isPending={skip.isPending}
            pendingLabel="Saving…"
            onPress={() => {
              skip.mutate();
            }}
          >
            Skip task
          </Button>
        ) : null}
        {mayRecord && task.status === 'pending' && !skipping ? (
          <Button
            isDisabled={!online}
            isPending={start.isPending}
            pendingLabel="Starting…"
            onPress={() => {
              start.mutate();
            }}
          >
            Start cleaning
          </Button>
        ) : null}
        {mayRecord && open && !skipping ? (
          <Button
            variant="primary"
            isDisabled={!online}
            isPending={complete.isPending}
            pendingLabel="Saving…"
            onPress={() => {
              complete.mutate();
            }}
          >
            {isBed ? 'Mark clean' : 'Mark done'}
          </Button>
        ) : null}
      </DialogActions>
    </Dialog>
  );
}
